import { useState } from "react";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery.ts";
import { BestPracticeCheckDocumentVersionRow, BestPracticeCheckViewModel } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.types.ts";
import { BestPracticeCheckResultDialogProps } from "@common/components/bestPracticeCheckResultDialog/BestPracticeCheckResultDialog.tsx";

export function useBestPracticeCheckResultDialog(props: BestPracticeCheckResultDialogProps) {
    const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

    const versionsUrl = `/document-version-best-practice-check/${props.documentId}/versions`;
    const { isLoading: isLoadingVersions, data: versions } = useDotLegalQuery<Array<BestPracticeCheckDocumentVersionRow>>(versionsUrl);

    const fileVersions = versions?.filter((v) => v.fileName && !v.link);

    const versionOptions = fileVersions?.map((v) => ({
        id: v.documentVersionId,
        name: `v${v.version} - ${v.fileName}`,
    }));

    const defaultResultUrl = `/document-version-best-practice-check/${props.documentId}`;
    const { isLoading: isLoadingDefaultResult, data: defaultResult } = useDotLegalQuery<BestPracticeCheckViewModel>(defaultResultUrl, {
        enabled: selectedVersionId === null && !props.initialResult,
    });

    const selectedVersion = selectedVersionId ? fileVersions?.find((v) => v.documentVersionId === selectedVersionId) : undefined;
    const selectedVersionHasCheck = selectedVersion?.hasBestPracticeCheck === true;
    const selectedVersionIsTooLargeForAi = selectedVersion?.isTooLargeForAi === true;

    const versionResultUrl = `/document-version-best-practice-check/${selectedVersionId}/version`;
    const { isLoading: isLoadingVersionResult, data: versionResult } = useDotLegalQuery<BestPracticeCheckViewModel>(versionResultUrl, {
        enabled: !!selectedVersionId && selectedVersionHasCheck,
    });

    const result = selectedVersionId === null ? (props.initialResult ?? defaultResult) : versionResult;
    const hasCheck = selectedVersionId === null ? !!result : selectedVersionHasCheck;
    const isLoadingData =
        isLoadingVersions ||
        (selectedVersionId === null && !props.initialResult && isLoadingDefaultResult) ||
        (!!selectedVersionId && selectedVersionHasCheck && isLoadingVersionResult);

    const showVersionDropdown = fileVersions && fileVersions.length > 1;

    return {
        isLoading: isLoadingData,
        result,
        selectedVersionId: selectedVersionId ?? result?.documentVersionId ?? null,
        setSelectedVersionId,
        hasCheck,
        selectedVersionIsTooLargeForAi,
        showVersionDropdown,
        versionOptions,
        versionsUrl,
    };
}
